import React, { useEffect, useState } from "react";
import { Box, Stack, Typography, Button } from "@mui/material";
import { LoadState } from "../../constants/enums";
import dummyImage from "../../assets/dummy_image.jpg";
import pdfImage from "../../assets/pdf.png";
import { getBodyPreviewHtml } from "../../helpers/metaTemplate";
import WhatsappBgImg from "../../assets/whatsapp_bg_img.jpg";
import { IWhatsAppTemplateData } from "../../redux/whatsappTemplate/whatsappTemplate.types";

interface PreviewTemplateProps {
  template: IWhatsAppTemplateData;
  loading?: LoadState;
  headerMedia?: File | null;
  headerType: string;
  headerVariableValue?: string;
  bodyVariableValues?: { [key: string]: string };
  bodyVariableTypes?: { [key: string]: string };
  bgImage?: boolean;
  createdByName: string;
}

export const ReplyPreviewTemplate: React.FC<PreviewTemplateProps> = ({
  template,
  loading = LoadState.Loaded,
  headerMedia,
  headerType,
  headerVariableValue,
  bodyVariableValues = {},
  bodyVariableTypes = {},
  bgImage = true,
  createdByName,
}) => {
  const [imageSource, setImageSource] = useState<string | null>(null);
  const [processedBody, setProcessedBody] = useState("");

  useEffect(() => {
    if (template.body?.text) {
      let bodyText = template.body.text;

      bodyText = bodyText.replace(/\{\{(\d+)\}\}/g, (match, index) => {
        const value = bodyVariableValues[index];
        if (value) {
          // If type is SELECT, wrap the value in curly braces
          if (bodyVariableTypes[index] === "SELECT") {
            return `{{${value}}}`;
          }
          return value;
        }
        return match;
      });

      Object.entries(bodyVariableValues).forEach(([key, value]) => {
        const regex = new RegExp(`\\{\\{${key}\\}\\}`, "g");
        // If type is SELECT, wrap the value in curly braces
        if (bodyVariableTypes[key] === "SELECT") {
          bodyText = bodyText.replace(regex, `{{${value}}}`);
        } else {
          bodyText = bodyText.replace(regex, value);
        }
      });

      setProcessedBody(bodyText);
    }
  }, [template, bodyVariableValues, bodyVariableTypes]);

  useEffect(() => {
    let source: string | null = null;

    // First priority: newly uploaded media (headerMedia File object)
    if (headerMedia instanceof File) {
      source = URL.createObjectURL(headerMedia);
    }
    // Second priority: header variable value (from form state)
    else if (headerVariableValue && !headerMedia) {
      source = headerVariableValue;
    }
    // Third priority: saved header_media data (from market campaign channel)
    else if (
      (template as any).header_media &&
      Array.isArray((template as any).header_media) &&
      (template as any).header_media.length > 0
    ) {
      source = (template as any).header_media[0].value;
    }
    // Fourth priority: saved attachment data (from market campaign channel)
    else if (
      (template as any).attachment &&
      (template as any).attachment !== ""
    ) {
      source = (template as any).attachment;
    }
    // Fifth priority: template.attachments
    else if (
      template.attachments &&
      template.attachments.length > 0 &&
      template.attachments[0]
    ) {
      source = template.attachments[0];
    }
    // Sixth priority: template.components with type HEADER
    else if (template.components && Array.isArray(template.components)) {
      const headerComponent = template.components.find(
        (comp: any) =>
          comp.type === "HEADER" &&
          (comp as any).example?.header_handle?.length > 0
      );
      if ((headerComponent as any)?.example?.header_handle?.[0]) {
        source = (headerComponent as any).example.header_handle[0];
      }
    }
    // Seventh priority: template.header examples
    else if (template.header?.example?.header_handle?.[0]) {
      const headerHandle = template.header.example.header_handle[0];
      if (typeof headerHandle === "string") {
        source = headerHandle;
      } else if (headerHandle) {
        source = headerHandle.url || headerHandle.path || null;
      }
    }
    // Finally use placeholders
    else if (headerType === "IMAGE") {
      source = dummyImage;
    } else if (headerType === "DOCUMENT") {
      source = "/images/document-placeholder.png";
    }

    setImageSource(source);

    return () => {
      if (headerMedia instanceof File && source) {
        URL.revokeObjectURL(source);
      }
    };
  }, [headerType, headerMedia, headerVariableValue, template]);

  const renderHeaderContent = () => {
    // Check for media URL in multiple places
    let attachmentUrl: string | null = null;

    // First priority: newly uploaded media (headerMedia File object)
    if (headerMedia instanceof File) {
      attachmentUrl = URL.createObjectURL(headerMedia);
    }
    // Second priority: header variable value (from form state)
    else if (headerVariableValue && !headerMedia) {
      attachmentUrl = headerVariableValue;
    }
    // Third priority: saved header_media data (from market campaign channel)
    else if (
      (template as any).header_media &&
      Array.isArray((template as any).header_media) &&
      (template as any).header_media.length > 0
    ) {
      attachmentUrl = (template as any).header_media[0].value;
    }
    // Fourth priority: saved attachment data (from market campaign channel)
    else if (
      (template as any).attachment &&
      (template as any).attachment !== ""
    ) {
      attachmentUrl = (template as any).attachment;
    }
    // Fifth priority: template.attachments
    else if (
      template.attachments &&
      template.attachments?.length > 0 &&
      template.attachments[0] !== ""
    ) {
      attachmentUrl = template.attachments[0];
    }
    // Sixth priority: template.components with type HEADER
    else if (template.components && Array.isArray(template.components)) {
      const headerComponent = template.components.find(
        (comp: any) =>
          comp.type === "HEADER" &&
          (comp as any).example?.header_handle?.length > 0
      );
      if ((headerComponent as any)?.example?.header_handle?.[0]) {
        attachmentUrl = (headerComponent as any).example.header_handle[0];
      }
    }
    // Seventh priority: template.header.example.header_handle
    else if (template.header?.example?.header_handle?.[0]) {
      const headerHandle = template.header.example.header_handle[0];
      if (typeof headerHandle === "string") {
        attachmentUrl = headerHandle;
      } else if (headerHandle) {
        attachmentUrl =
          (headerHandle as any).url || (headerHandle as any).path || null;
      }
    }

    // Ensure attachmentUrl is a string before processing
    if (attachmentUrl && typeof attachmentUrl !== "string") {
      console.warn("attachmentUrl is not a string:", attachmentUrl);
      attachmentUrl = String(attachmentUrl); // Convert to string or handle appropriately
    }

    if (attachmentUrl) {
      const s3BaseUrl = "https://enr-biolerplate.s3.ap-south-1.amazonaws.com/";

      if (
        !attachmentUrl?.startsWith(s3BaseUrl) &&
        !attachmentUrl?.startsWith("http")
      ) {
        attachmentUrl = `${s3BaseUrl}${attachmentUrl}`;
      }

      // Get file extension to determine if it's an image, video, or document
      const fileName = attachmentUrl.split("/").pop() || "";
      const fileExt = fileName.split(".").pop()?.toLowerCase() || "";
      const isImageFile = [
        "jpg",
        "jpeg",
        "png",
        "gif",
        "bmp",
        "webp",
        "svg",
        "ico",
        "tiff",
        "tif",
      ].includes(fileExt);
      const isVideoFile = [
        "mp4",
        "avi",
        "mov",
        "wmv",
        "flv",
        "mkv",
        "webm",
      ].includes(fileExt);
      const isDocumentFile = [
        "pdf",
        "doc",
        "docx",
        "xls",
        "xlsx",
        "txt",
        "ppt",
        "pptx",
        "rtf",
        "odt",
        "ods",
        "odp",
      ].includes(fileExt);

      // Check if we have content to render (either from template.content or template.body.text)
      const hasContent = template.content || template.body?.text;
      if (hasContent) {
        // Render based on file type
        if (isImageFile) {
          return (
            <img
              src={attachmentUrl}
              alt="Attachment"
              style={{
                borderRadius: 2,
                objectFit: "cover",
                width: "100px",
                maxHeight: "70px",
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).src = dummyImage;
              }}
            />
          );
        } else if (isVideoFile) {
          return (
            <Stack spacing={1} direction={"column"}>
              <video
                controls
                style={{
                  borderRadius: 2,
                  width: "100px",
                  maxHeight: "70px",
                }}
              >
                <source src={attachmentUrl} type={`video/${fileExt}`} />
                Your browser does not support the video tag.
              </video>
            </Stack>
          );
        } else if (isDocumentFile) {
          const fileBaseName = fileName.replace(/\.[^/.]+$/, ""); // Remove extension

          // Determine icon and file type text based on extension
          let fileTypeText = "DOCUMENT";

          if (fileExt === "pdf") {
            fileTypeText = "PDF";
          } else if (fileExt === "doc" || fileExt === "docx") {
            fileTypeText = "DOC";
          } else if (fileExt === "xls" || fileExt === "xlsx") {
            fileTypeText = "XLS";
          }

          // Mock file size - in a real app you'd get this from the file metadata
          const fileSize = Math.floor(Math.random() * 900) + 100; // Random size between 100-1000 KB
          const sizeText =
            fileSize < 1024
              ? `${fileSize} KB`
              : `${(fileSize / 1024).toFixed(1)} MB`;

          return (
            <Box
              onClick={() =>
                attachmentUrl && window.open(attachmentUrl, "_blank")
              }
              sx={{
                display: "flex",

                alignItems: "end",

                backgroundColor: "#0b355c",
                borderRadius: "7.5px",
                width: "100px",
                maxHeight: "70px",
                p: "10px",
                cursor: "pointer",
                "&:hover": {
                  // backgroundColor: "#e5e5e7",
                },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <img src={pdfImage} alt="pdf" width={"26px"} height={"26px"} />

                <Stack sx={{ overflow: "hidden" }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 500,
                      color: "white",
                      maxWidth: "200px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {fileBaseName}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#bcc2c5ff",
                    }}
                  >
                    {fileTypeText} • {sizeText}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          );
        }
      }

      if (headerType === "DOCUMENT" && !headerMedia) {
        const fileName = attachmentUrl.split("/").pop() || "document";
        const fileExt = fileName.split(".").pop()?.toLowerCase() || "";
        const fileBaseName = fileName.replace(/\.[^/.]+$/, ""); // Remove extension

        // Determine icon and file type text based on extension
        let fileTypeText = "DOCUMENT";

        if (fileExt === "pdf") {
          fileTypeText = "PDF";
        } else if (fileExt === "doc" || fileExt === "docx") {
          fileTypeText = "DOC";
        } else if (fileExt === "xls" || fileExt === "xlsx") {
          fileTypeText = "XLS";
        }

        // Mock file size - in a real app you'd get this from the file metadata
        const fileSize = Math.floor(Math.random() * 900) + 100; // Random size between 100-1000 KB
        const sizeText =
          fileSize < 1024
            ? `${fileSize} KB`
            : `${(fileSize / 1024).toFixed(1)} MB`;

        return (
          <Box
            onClick={() =>
              attachmentUrl && window.open(attachmentUrl, "_blank")
            }
            sx={{
              display: "flex",

              alignItems: "end",

              // backgroundColor: "#072745",
              backgroundColor: "primary.main",
              borderRadius: "7.5px",
              width: "100px",
              maxHeight: "70px",
              p: "10px",

              cursor: "pointer",
              "&:hover": {
                // backgroundColor: "#e5e5e7",
              },
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              {/* <FileIcon sx={{ color: "#b90e13" }} /> */}
              <img src={pdfImage} alt="pdf" width={"26px"} height={"26px"} />

              <Stack sx={{ overflow: "hidden" }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,

                    color: "white",
                    maxWidth: "200px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {fileBaseName}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#bcc2c5ff",
                  }}
                >
                  {fileTypeText} • {sizeText}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        );
      } else if (headerType === "IMAGE" && !headerMedia) {
        return (
          <img
            src={attachmentUrl}
            alt="Attachment"
            style={{
              borderRadius: 2,
              objectFit: "cover",
              width: "100px",
              maxHeight: "70px",
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).src = dummyImage;
            }}
          />
        );
      } else if (headerType === "IMAGE" && headerMedia) {
        return (
          <img
            // src={attachmentUrl}
            src={headerMedia ? URL.createObjectURL(headerMedia) : attachmentUrl}
            alt="Attachment"
            style={{
              borderRadius: 2,
              objectFit: "cover",
              width: "100px",
              maxHeight: "70px",
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).src = dummyImage;
            }}
          />
        );
      } else if (headerType === "VIDEO" && !headerMedia) {
        return (
          <Stack spacing={1} direction={"column"}>
            <video
              controls
              style={{
                width: "100%",
                borderRadius: 2,
                maxHeight: 300,
              }}
            >
              <source src={attachmentUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </Stack>
        );
      } else if (headerType === "VIDEO" && headerMedia) {
        return (
          <Stack spacing={1} direction={"column"}>
            <video
              controls
              style={{
                borderRadius: 2,
                width: "100px",
                maxHeight: "70px",
              }}
            >
              <source
                src={
                  headerMedia ? URL.createObjectURL(headerMedia) : attachmentUrl
                }
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
          </Stack>
        );
      }
    }

    if (!imageSource) return null;

    if (headerType === "IMAGE") {
      return (
        <Stack spacing={1} direction={"column"}>
          <img
            src={imageSource}
            alt={template.header?.text || "Header image"}
            style={{
              borderRadius: 2,
              objectFit: "cover",
              width: "100px",
              maxHeight: "70px",
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).src = dummyImage;
            }}
          />
        </Stack>
      );
    } else if (headerType === "VIDEO") {
      return (
        <Stack spacing={1} direction={"column"}>
          <video
            controls
            style={{
              width: "100px",
              maxHeight: "70px",
              borderRadius: 2,
            }}
          >
            <source src={imageSource} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </Stack>
      );
    } else if (headerType === "DOCUMENT") {
      return (
        <iframe
          src={imageSource}
          style={{ width: "100%", height: "100%", border: "none" }}
          title="Document"
          onClick={() => {
            // Handle document download/view
            if (imageSource) {
              window.open(imageSource, "_blank");
            }
          }}
        />
      );
    }
    return null;
  };

  return (
    <Box
      borderRadius={2}
      maxWidth={headerType === "DOCUMENT" ? "300px" : "100%"}
      sx={
        bgImage
          ? {
              backgroundImage: `url(${WhatsappBgImg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              height: "100%",
              display: "flex",
              alignItems: "flex-end",
            }
          : {
              display: "flex",
              flexDirection: "column",
            }
      }
    >
      <Typography variant="caption" sx={{ fontWeight: 600, color: "#fff" }}>
        {createdByName}
      </Typography>
      <Stack
        sx={{
          py: 1,
          //   px: 2,

          position: "relative",
          width: "100%", // Ensure it takes full width of parent

          marginLeft: "8px",
          "&:before": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: "-8px",

            width: "8px",
            height: "16px",
            backgroundColor: "inherit",
            clipPath: "polygon(0 0, 100% 0, 100% 100%)",
          },
        }}
      >
        {template.header?.text && (
          <Stack spacing={1} mt={2} direction={"column"}>
            <Typography variant="body1" fontSize={"1rem"} fontWeight={600}>
              {template.header.text}
            </Typography>
          </Stack>
        )}

        <Stack direction={"row"} spacing={2}>
          <Stack spacing={1} direction={"column"}>
            <Typography
              variant="body1"
              sx={{
                p: { margin: 0 },
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  maxWidth: "400px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  verticalAlign: "bottom",
                }}
                dangerouslySetInnerHTML={{
                  __html:
                    processedBody ||
                    getBodyPreviewHtml(
                      template.body?.text || "",
                      template.example?.body_text
                    ),
                }}
              />
            </Typography>
          </Stack>
          {renderHeaderContent()}
        </Stack>

        {template.footer?.text && (
          <Stack spacing={1} mt={2} direction={"column"}>
            <Typography variant="body1">{template.footer.text}</Typography>
          </Stack>
        )}

        {template.buttons?.buttons && template.buttons?.buttons?.length > 0 && (
          <Stack direction="column" mt={1} spacing={1}>
            {template.buttons?.buttons
              ?.filter((x) => x.text)
              .map((button, index) => (
                <Button
                  key={index}
                  sx={{
                    width: "100%",
                    backgroundColor: "#808080",
                    "&:hover": { backgroundColor: "#808080" },
                    color: "#fff",
                  }}
                >
                  {button.text}
                </Button>
              ))}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};
